import { IHotspotCard } from '@models/IHotspotCard';
import { IInspirationCard } from '@models/IInspirationCard';
import { cardTypes, IListingFilterGroup } from '@models/IListing';
import { CardData, SelectedFilter } from '@models/IListingWrapper';

export interface ListingWrapperState {
  readonly listItems: cardTypes;
  readonly slicedListItems: cardTypes;
  readonly isMobileFiltersOpen: boolean;
  readonly selectedFilters: SelectedFilter[];
  readonly sortedListingFilters: IListingFilterGroup[];
  readonly showLoadMore: boolean;
  readonly selectedFeaturedInspiration?: IHotspotCard;
  readonly selectedInspirationCard?: IInspirationCard;
  readonly promoCardReferences: number[];
  readonly clearSearch: number;
  readonly hotspotData: CardData;
  readonly inspirationData: CardData;
}

export const enum ActionType {
  LIST_ITEMS = 'LIST_ITEMS',
  APPEND_LIST_ITEMS = 'APPEND_LIST_ITEMS',
  SLICED_LIST_ITEMS = 'SLICED_LIST_ITEMS',
  MOBILE_FILTERS_OPEN = 'MOBILE_FILTERS_OPEN',
  SELECTED_FILTERS = 'SELECTED_FILTERS',
  SORTED_LISTING_FILTERS = 'SORTED_LISTING_FILTERS',
  SHOW_LOAD_MORE = 'SHOW_LOAD_MORE',
  SELECTED_FEATURED_INSPIRATION = 'SELECTED_FEATURED_INSPIRATION',
  SELECTED_INSPIRATION_CARD = ' SELECTED_INSPIRATION_CARD',
  PROMO_CARD_REFERENCES = 'PROMO_CARD_REFERENCES',
  CLEAR_SEARCH = 'CLEAR_SEARCH',
  HOTSPOT_DATA = 'HOTSPOT_DATA',
  INSPIRATION_DATA = 'INSPIRATION_DATA',
}

interface ListItemsAction {
  readonly type: ActionType.LIST_ITEMS;
  readonly payload: cardTypes;
}
interface AppendListItemsAction {
  readonly type: ActionType.APPEND_LIST_ITEMS;
  readonly payload: cardTypes;
}
interface SlicedListItemsAction {
  readonly type: ActionType.SLICED_LIST_ITEMS;
  readonly payload: cardTypes;
}
interface MobileFiltersOpenAction {
  readonly type: ActionType.MOBILE_FILTERS_OPEN;
  readonly payload: boolean;
}
interface SelectedFiltersAction {
  readonly type: ActionType.SELECTED_FILTERS;
  readonly payload: SelectedFilter[];
}
interface SortedListingFiltersAction {
  readonly type: ActionType.SORTED_LISTING_FILTERS;
  readonly payload: IListingFilterGroup[];
}
interface ShowLoadMoreAction {
  readonly type: ActionType.SHOW_LOAD_MORE;
  readonly payload: boolean;
}
interface SelectedFeaturedInspirationAction {
  readonly type: ActionType.SELECTED_FEATURED_INSPIRATION;
  readonly payload: IHotspotCard;
}
interface SelectedInspirationCardAction {
  readonly type: ActionType.SELECTED_INSPIRATION_CARD;
  readonly payload: IInspirationCard;
}
interface PromoCardReferencesAction {
  readonly type: ActionType.PROMO_CARD_REFERENCES;
  readonly payload: number[];
}
interface ClearSearchAction {
  readonly type: ActionType.CLEAR_SEARCH;
  readonly payload: number | 'increment';
}
interface HotspotDataAction {
  readonly type: ActionType.HOTSPOT_DATA;
  readonly payload: CardData;
}
interface InspirationDataAction {
  readonly type: ActionType.INSPIRATION_DATA;
  readonly payload: CardData;
}

export type Action =
  | ListItemsAction
  | AppendListItemsAction
  | SlicedListItemsAction
  | MobileFiltersOpenAction
  | SelectedFiltersAction
  | SortedListingFiltersAction
  | ShowLoadMoreAction
  | SelectedFeaturedInspirationAction
  | SelectedInspirationCardAction
  | PromoCardReferencesAction
  | ClearSearchAction
  | HotspotDataAction
  | InspirationDataAction;

export const initialState: ListingWrapperState = {
  listItems: [],
  slicedListItems: [],
  isMobileFiltersOpen: false,
  selectedFilters: [],
  sortedListingFilters: [],
  showLoadMore: false,
  selectedFeaturedInspiration: undefined,
  selectedInspirationCard: undefined,
  promoCardReferences: [],
  clearSearch: 0,
  hotspotData: {
    show: false,
    occupiedSpaceCount: 0,
    aspectRatio: undefined,
  },
  inspirationData: {
    show: false,
    occupiedSpaceCount: 0,
  },
};

export const reducer = (
  state: ListingWrapperState,
  action: Action
): ListingWrapperState => {
  switch (action.type) {
    case ActionType.LIST_ITEMS:
      return {
        ...state,
        listItems: action.payload,
      };
    case ActionType.APPEND_LIST_ITEMS:
      return {
        ...state,
        listItems: [...(state.listItems ?? []), ...(action.payload ?? [])],
      };
    case ActionType.SLICED_LIST_ITEMS:
      return {
        ...state,
        slicedListItems: action.payload,
      };
    case ActionType.MOBILE_FILTERS_OPEN:
      return {
        ...state,
        isMobileFiltersOpen: action.payload,
      };
    case ActionType.SELECTED_FILTERS:
      return {
        ...state,
        selectedFilters: action.payload,
      };
    case ActionType.SORTED_LISTING_FILTERS:
      return {
        ...state,
        sortedListingFilters: action.payload,
      };
    case ActionType.SHOW_LOAD_MORE:
      return {
        ...state,
        showLoadMore: action.payload,
      };
    case ActionType.SELECTED_FEATURED_INSPIRATION:
      return {
        ...state,
        selectedFeaturedInspiration: action.payload,
      };
    case ActionType.SELECTED_INSPIRATION_CARD: {
      return {
        ...state,
        selectedInspirationCard: action.payload,
      };
    }
    case ActionType.PROMO_CARD_REFERENCES:
      return {
        ...state,
        promoCardReferences: action.payload,
      };
    case ActionType.CLEAR_SEARCH:
      return {
        ...state,
        clearSearch:
          action.payload === 'increment'
            ? state.clearSearch + 1
            : action.payload,
      };
    case ActionType.HOTSPOT_DATA:
      return {
        ...state,
        hotspotData: action.payload,
      };
    case ActionType.INSPIRATION_DATA:
      return {
        ...state,
        inspirationData: action.payload,
      };
    default:
      return { ...initialState };
  }
};
